/**
 * cypSafeguardingAlert.js
 *
 * Client-side detection for the CYP ADHD safeguarding notice dialog.
 *
 * Mirrors the server-side transition logic in
 * python-functions/functions/cyp_adhd_safeguarding_alert.py: only NEWLY
 * selected flagged options trigger the notice. The source of truth for
 * which options are flagged is the `safeguardingAlertOptions` marker
 * stamped on the riskBehaviours subquestion by the form definition
 * (constants/cyp_adhd/safeguarding.py). The client reads it from the
 * formInstance snapshot at runtime — no cross-language duplication.
 *
 * Pre-backfill formInstances that lack the marker degrade gracefully:
 * the server trigger still sends the email; only the in-platform notice
 * is suppressed.
 *
 * CONTENT OWNERSHIP: notice copy must cross Tom (clinical) and Jasmine
 * (comprehension) before release. The copy constants below must match
 * the Python constants in constants/cyp_adhd/safeguarding.py — a Python
 * test asserts the two files are identical.
 */

// ── Notice copy constants ───────────────────────────────────────────────
// Shared between the dialog component and the Python email builder's
// "What the parent or carer was shown" section.

/** Dialog title. */
export const CYP_SAFEGUARDING_NOTICE_TITLE =
  'Thank you for telling us about this';

/**
 * Dialog body, with {organisation} token resolved at render time.
 * Must exactly match CYP_SAFEGUARDING_NOTICE_BODY in safeguarding.py.
 */
export const CYP_SAFEGUARDING_NOTICE_BODY =
  'Because of the answers you have given, the clinical team at ' +
  '{organisation} has been told so they can look at this information. ' +
  'You do not need to do anything else right now.';

/**
 * Emergency line, with {child_name} token resolved at render time.
 * Must exactly match CYP_SAFEGUARDING_NOTICE_EMERGENCY in safeguarding.py.
 */
export const CYP_SAFEGUARDING_NOTICE_EMERGENCY =
  "If you are worried about {child_name}'s safety right now, " +
  'contact your GP, call NHS 111, or call 999 in an emergency.';

// ── Detection helper ────────────────────────────────────────────────────

/**
 * Extract the selected option strings from a selectMultiple answer.
 *
 * Handles the live `{selectedOptions: [...]}` shape, a bare array, a
 * string, and null/undefined.
 *
 * @param {*} answer
 * @returns {string[]}
 */
function extractSelectedOptions(answer) {
  if (answer == null) return [];
  if (typeof answer === 'object' && !Array.isArray(answer)) {
    const inner = answer.selectedOptions;
    return Array.isArray(inner)
      ? inner.filter((o) => typeof o === 'string')
      : [];
  }
  if (Array.isArray(answer)) {
    return answer.filter((o) => typeof o === 'string');
  }
  if (typeof answer === 'string' && answer) return [answer];
  return [];
}

/**
 * Return the flagged options that were NEWLY selected on the current save.
 *
 * Reads the `safeguardingAlertOptions` marker from the question's
 * subquestion definition (the formInstance snapshot). Returns an empty
 * array when nothing new is flagged — the notice should not open.
 *
 * @param {object} question - the question object from the form definition
 *   (with `subquestions` map)
 * @param {object} prevAnswers - answers BEFORE this save (rawFormInstance.answers)
 * @param {object} nextAnswers - answers AFTER this save (mergedAllAnswers)
 * @returns {string[]} newly flagged options, in schema order
 */
export function getNewlyFlaggedSafeguardingOptions(
  question,
  prevAnswers,
  nextAnswers,
) {
  if (!question?.subquestions) return [];
  const prev = prevAnswers || {};
  const next = nextAnswers || {};

  const results = [];

  for (const sq of Object.values(question.subquestions)) {
    const alertOptions = sq.safeguardingAlertOptions;
    if (!alertOptions || !Array.isArray(alertOptions) || alertOptions.length === 0) {
      continue;
    }
    const varName = sq.serviceUserVarName;
    if (!varName) continue;

    const beforeSet = new Set(extractSelectedOptions(prev[varName]));
    const afterSet = new Set(extractSelectedOptions(next[varName]));
    const alertSet = new Set(alertOptions);

    // Preserve schema order: iterate alertOptions, keep only newly selected.
    for (const opt of alertOptions) {
      if (alertSet.has(opt) && afterSet.has(opt) && !beforeSet.has(opt)) {
        results.push(opt);
      }
    }
  }

  return results;
}
